import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import Link from '@mui/material/Link';

import {
  formatDate,
  formatCurrency,
} from '#common/utils';
import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import {
  DEFAULT_TIME_RANGE_PRESET,
  TimeRangeValue,
  resolveDateRangeParam,
} from '#components/time-range.utils';
import {
  useGetInvoicesQuery,
} from '#services/invoice';
import {
  KEY_CODES,
} from '#utils/constants';
import URLSearchParamsEx from '#utils/url-ex';
import {
  setDateRange,
  setFilter,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
} from './list.slice';

interface InvoiceRow {
  invoiceNumber: string | number;
  invoiceDate: string;
  invoiceTotal: number;
  orderId: string | number;
  [key: string]: unknown;
}

interface InvoiceListSlice {
  dateRange: TimeRangeValue;
  filter: string;
  filterKey: string;
  order: 'asc' | 'desc';
  orderBy: string;
  pageIndex: number;
  rows: EnhancedTableRow[];
  rowCount: number;
  pageSize: number;
}

export default function InvoiceList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('invoice');

  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      meta.setAttribute('content', 'width=device-width, initial-scale=1.0');
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'viewport';
      newMeta.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(newMeta);
    }

    return () => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.remove();
      }
    };
  }, []);

  const userInfo = useSelector((state: { auth: { userInfo: { isCustomer?: boolean } } }) => state.auth.userInfo);
  const filter = useSelector((state: { invoiceList: InvoiceListSlice }) => state.invoiceList.filter);
  const dateRange = useSelector((state: { invoiceList: InvoiceListSlice }) => state.invoiceList.dateRange);
  const filterKey = useSelector((state: { invoiceList: InvoiceListSlice }) => state.invoiceList.filterKey);
  const order = useSelector((state: { invoiceList: InvoiceListSlice }) => state.invoiceList.order);
  const orderBy = useSelector((state: { invoiceList: InvoiceListSlice }) => state.invoiceList.orderBy);
  const pageIndex = useSelector((state: { invoiceList: InvoiceListSlice }) => state.invoiceList.pageIndex);
  const rows = useSelector((state: { invoiceList: InvoiceListSlice }) => state.invoiceList.rows);
  const rowCount = useSelector((state: { invoiceList: InvoiceListSlice }) => state.invoiceList.rowCount);
  const pageSize = useSelector((state: { invoiceList: InvoiceListSlice }) => state.invoiceList.pageSize);
  const [filterForQuery, setFilterForQuery] = useState(filter);
  const [isInitialMount, setIsInitialMount] = useState(true);

  useGetInvoicesQuery({
    dateRange: resolveDateRangeParam('invoiceDate', dateRange),
    order,
    orderBy,
    filter: filterForQuery,
    filterKey,
    // headerFilterKeys,
    pageIndex,
    pageSize,
  }, {
    refetchOnMountOrArgChange: true,
  });

  // Restore state from URL parameters on component mount only
  useEffect(() => {
    if (!isInitialMount) return;

    const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    if (urlParams.values.filter !== undefined) {
      dispatch(setFilter(urlParams.values.filter as string));
      setFilterForQuery(urlParams.values.filter as string);
    }
    if (urlParams.values.order !== undefined) {
      dispatch(setOrder(urlParams.values.order as string));
    }
    if (urlParams.values.orderBy !== undefined) {
      dispatch(setOrderBy(urlParams.values.orderBy as string));
    }
    if (urlParams.values.pageSize !== undefined) {
      dispatch(setPageSize(urlParams.values.pageSize as unknown as number));
    }

    setIsInitialMount(false);
  }, [location.search, isInitialMount, dispatch]);

  function createDownloadInvoiceHandler(invoice: InvoiceRow) {
    return () => {
      const url = `/api/downloadInvoice?invoiceNumber=${invoice.invoiceNumber}`;
      window.open(url);
    };
  }

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'invoiceNumber',
    label: t('table.invoiceNumber'),
    renderer: (row, _idx) => {
      const invoice = row as unknown as InvoiceRow;
      return invoice.invoiceNumber;
      // const invoiceIndex = pageIndex * pageSize + idx + 1;
      // const qs = new URLSearchParamsEx({
      //   order,
      //   orderBy,
      //   filterKey,
      //   filter,
      //   // headerFilters,
      //   invoiceNumber: row.invoiceNumber,
      //   invoiceIndex,
      // });
      // const url = `/management/invoice/detail?${qs}`;
      // return (
      //   <Box>
      //     <Link
      //       component="button"
      //       onClick={() => {
      //         navigate(url);
      //       }}
      //     >
      //       {row.invoiceNumber}
      //     </Link>
      //   </Box>
      // );
    },
  }, {
    id: 'invoiceDate',
    label: t('table.invoiceDate'),
    renderer: (row) => formatDate((row as unknown as InvoiceRow).invoiceDate),
  }, {
    id: 'invoiceTotal',
    label: t('table.total'),
    renderer: (row) => formatCurrency((row as unknown as InvoiceRow).invoiceTotal),
  }, {
    id: 'orderId',
    label: t('table.orderNumber'),
    renderer: (row) => {
      const invoice = row as unknown as InvoiceRow;
      const url = userInfo.isCustomer
        ? `/order/detail?orderId=${invoice.orderId}`
        : `/management/order/detail?orderId=${invoice.orderId}`;
      return (
        <Box>
          <Link
            component="button"
            onClick={() => {
              void navigate(url);
            }}
          >
            {invoice.orderId}
          </Link>
        </Box>
      );
    },
  }, {
    id: 'action',
    label: t('table.action'),
    renderer: (row) => (
      <Link
        key="btn-download"
        component="button"
        onClick={createDownloadInvoiceHandler(row as unknown as InvoiceRow)}
      >
        {t('download')}
      </Link>
    ),
  }];

  const tableProps: EnhancedTableProps = {
    name: 'variants',

    headers: tableHeaders,

    enableSort: true,

    order,
    setOrder,
    orderBy,
    setOrderBy,

    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize,
    rowCount,

    timeRange: {
      value: dateRange,
      onChange: (value) => dispatch(setDateRange(value)),
      columnLabel: 'Invoice Date',
      defaultPreset: DEFAULT_TIME_RANGE_PRESET,
      entityLabel: 'invoices',
    },

    rows,
  };

  return (
    <Box sx={{ py: { xs: 3, sm: 5 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant={'h7' as TypographyProps['variant']} sx={{ fontWeight: 'fontWeightBold' as unknown as number }}>
          {t('title')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Paper
          elevation={0}
          variant="outlined"
          square
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: { xs: '100%', sm: 400 },
            mb: 1,
          }}
        >
          <IconButton type="button" sx={{ p: '10px' }} aria-label={t('searchAria')}>
            <SearchIcon />
          </IconButton>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder={t('searchPlaceholder')}
            value={filter}
            onChange={(e) => {
              dispatch(setFilter(e.target.value));
            }}
            onKeyUp={(e) => {
              if (e.keyCode === KEY_CODES.ENTER || !filter.length) {
                setFilterForQuery(filter);
              }
            }}
          />
        </Paper>
      </Box>

      <EnhancedTable
        {...tableProps}
      />
    </Box>
  );
}
